import { Button, Dropdown, message, Modal, Popover, Space } from "antd";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, type TableColumnProps } from "zhihao-ui-react";
import type { CampaignRegistrationVo, File as EntityFile } from "@zhihaoscm/entity";
import { CAMPAIGN_REGISTRATION_STATE, ORDER_PAY_TYPE, PARTICIPATION, PC_ACTIVITY_STATE } from "@zhihaoscm/meta";
import { TABLE_COLUMN_WIDTH, formatCurrency } from "@zhihaoscm/utils";
import {
    Breadcrumb,
    DetailHeader,
    DetailJson,
    ErrorState,
    ProTable,
    type DetailJsonBlock,
    type PageParams,
    type ProColumn
} from "@/components/common";
import { campaignRegistrationDetail, campaignRegistrationPagingAll } from "@/api/campaignRegistration";
import { fileService } from "@/api/fileService";
import { openPublicPage } from "@/utils/publicPage";
import { RegistrationEditDialog, RegistrationPaymentDialog } from "./RegistrationDialogs";
import "./registration.css";

/** 金额直接复用 Vue 使用的公共格式化函数，整数不额外补 `.00`。 */
export const formatRegistrationFee = (value?: number) => formatCurrency(value, "--");
/** 报名人员摘要，超过一人时保留首人并显示人数差。 */
function ApplicantLine({ applicant }: { applicant: CampaignRegistrationVo["campaignRegistrationApplicant"][number] }) {
    const values = [applicant.position, applicant.name, applicant.mobile].filter(Boolean);
    return (
        <div className="registration-attendee-info">
            {values.map((value, index) => (
                <span key={`${value}-${index}`}>
                    {index ? <i className="registration-attendee-divide" /> : null}
                    {value}
                </span>
            ))}
        </div>
    );
}
/** 详情页沿用旧版姓名、职务、手机号顺序。 */
function DetailApplicantLine({
    applicant
}: {
    applicant: CampaignRegistrationVo["campaignRegistrationApplicant"][number];
}) {
    const values = [applicant.name, applicant.position, applicant.mobile].filter(Boolean);
    return (
        <div className="registration-attendee-info">
            {values.map((value, index) => (
                <span key={`${value}-${index}`}>
                    {index ? <i className="registration-attendee-divide" /> : null}
                    {value}
                </span>
            ))}
        </div>
    );
}
/** 报名人员摘要使用旧版首人展示与多人悬浮清单。 */
function ApplicantSummary({ row }: { row: CampaignRegistrationVo }) {
    const applicants = row.campaignRegistrationApplicant ?? [];
    if (!applicants.length) return <>--</>;
    const summary = (
        <div className="registration-attendee-summary">
            <ApplicantLine applicant={applicants[0]} />
            {applicants.length > 1 ? <span className="registration-attendee-tag">+{applicants.length - 1}</span> : null}
        </div>
    );
    return applicants.length > 1 ? (
        <Popover
            placement="bottom"
            content={
                <div className="registration-attendee-popover">
                    {applicants.map(item => (
                        <ApplicantLine key={item.id} applicant={item} />
                    ))}
                </div>
            }
        >
            {summary}
        </Popover>
    ) : (
        summary
    );
}

interface RegistrationAction {
    key: string;
    label: string;
    onClick: () => void;
}

/** 旧版 buttons.vue 在动作超过三个时仅平铺前两个，其余收进“更多”。 */
export function splitRegistrationActions(actions: RegistrationAction[]) {
    return actions.length > 3 ? { visible: actions.slice(0, 2), more: actions.slice(2) } : { visible: actions, more: [] };
}

/** 使用 Ant Design 原生 Dropdown 对应 Vue 的 el-dropdown，不自行绘制菜单。 */
function RegistrationActionButtons({ actions }: { actions: RegistrationAction[] }) {
    const { visible, more } = splitRegistrationActions(actions);
    return (
        <div className="registration-operation">
            {visible.map(action => (
                <Button key={action.key} type="link" onClick={action.onClick}>
                    {action.label}
                </Button>
            ))}
            {more.length ? (
                <Dropdown
                    overlayClassName="registration-operation-dropdown"
                    placement="bottom"
                    menu={{
                        items: more.map(action => ({ key: action.key, label: action.label })),
                        onClick: ({ key }) => more.find(action => action.key === key)?.onClick()
                    }}
                >
                    <Button type="link">更多</Button>
                </Dropdown>
            ) : null}
        </div>
    );
}

/**
 * 凭证预览严格复用 Vue fileDialog 的列结构：序号、名称、格式、上传时间和操作。
 * 列表页与详情页共用，避免两个入口再次出现展示差异。
 */
function RegistrationFileDialog({
    files,
    title,
    onClose
}: {
    files: EntityFile[];
    title: string;
    onClose: () => void;
}) {
    /** 旧 Vue fileDialog 直接使用 ZhTable，列定义继续使用同一套组件协议。 */
    const columns: TableColumnProps<EntityFile>[] = [
        { label: "序号", key: "index", width: 60, align: "center", render: ({ rowIndex }) => rowIndex + 1 },
        { label: "文件名称", prop: "name" },
        { label: "文件格式", prop: "type", width: 160 },
        { label: "上传时间", prop: "createdTime", width: 160, formatter: row => row.createdTime ? moment(row.createdTime).format("YYYY-MM-DD HH:mm") : "--" },
        { label: "操作", key: "operation", width: 100, fixed: "right", render: ({ row }) => <Button href={row.path} target="_blank" type="link">查看/下载</Button> }
    ];
    return (
        <Modal className="registration-file-dialog" footer={null} open={Boolean(title)} title={title} width={800} onCancel={onClose}>
            <Table
                data={files}
                pagination={false}
                rowKey="id"
                columns={columns}
            />
        </Modal>
    );
}

/** 合同下载保持 Vue 的 fetch/blob 流程，兼容带鉴权或跨域响应的文件地址。 */
async function downloadRegistrationContract(fileId: number) {
    const file = await fileService.getFileById(fileId);
    const response = await fetch(file.path);
    if (!response.ok) throw new Error("下载失败");
    const url = URL.createObjectURL(await response.blob());
    const anchor = document.createElement("a");
    const parts = file.name.split(".");
    const extension = parts.pop();
    anchor.href = url;
    anchor.download = `${parts.join(".")}_${moment().format("YYYYMMDDHHmmss")}.${extension}`;
    document.body.appendChild(anchor);
    anchor.click();
    window.setTimeout(() => {
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    }, 100);
}

/** 报名列表完整映射旧表格查询字段、枚举、详情入口和状态动作。 */
export function RegistrationListPage() {
    const navigate = useNavigate();
    const [revision, setRevision] = useState(0);
    const [activeRow, setActiveRow] = useState<CampaignRegistrationVo>();
    const [dialog, setDialog] = useState<"payment" | "edit">();
    const [files, setFiles] = useState<EntityFile[]>([]);
    const [fileTitle, setFileTitle] = useState("");
    const request = useMemo(
        () => async (params: PageParams) => {
            const response = await campaignRegistrationPagingAll(params as never);
            return response.data ?? { list: [], total: 0 };
        },
        [revision]
    );
    const openDetail = (row: CampaignRegistrationVo) => {
        const code = row.campaignRegistration?.code;
        if (code) navigate(`/registration/myRegistrationDetail?id=${encodeURIComponent(code)}`);
    };
    const loadDialogRow = async (row: CampaignRegistrationVo, nextDialog: "payment" | "edit") => {
        const response = await campaignRegistrationDetail(row.campaignRegistration.code);
        setActiveRow(response.data);
        setDialog(nextDialog);
    };
    const viewFiles = async (ids: number[] | undefined, title: string) => {
        if (!ids?.length) {
            void message.error("暂无支付凭证");
            return;
        }
        setFiles(await fileService.getFileByIds(ids));
        setFileTitle(title);
    };
    const downloadContract = async (row: CampaignRegistrationVo) => {
        const config = row.meetingTypeConfig?.find(item => item.type === row.campaignRegistration.meetingType);
        if (!config?.contractTemplateFileId) return;
        await downloadRegistrationContract(config.contractTemplateFileId);
    };
    const columns: ProColumn<CampaignRegistrationVo>[] = [
        {
            key: "index",
            title: "序号",
            width: 80,
            align: "center",
            className: "table-index-center",
            render: (_value, _row, index) => index + 1
        },
        {
            key: "code",
            title: "报名编号",
            width: TABLE_COLUMN_WIDTH.WORD9,
            render: (_value, row) => (
                <Button className="registration-link" type="link" onClick={() => openDetail(row)}>
                    {row.campaignRegistration.code}
                </Button>
            )
        },
        {
            key: "title",
            title: "活动名称",
            width: 200,
            search: { key: "name", placeholder: "请输入活动名称" },
            render: (_value, row) =>
                row.hasSupportJumpCampaign ? (
                    <Button
                        className="registration-campaign-link"
                        type="link"
                        onClick={() => openPublicPage(`/public/active?id=${row.campaign.code}`)}
                    >
                        {row.campaign.title}
                    </Button>
                ) : (
                    <span className="registration-campaign-title">{row.campaign.title ?? "--"}</span>
                )
        },
        {
            key: "meetingType",
            title: "参会形式",
            width: TABLE_COLUMN_WIDTH.WORD9,
            search: {
                key: "meetingType",
                type: "select",
                placeholder: "请选择参会形式",
                options: PARTICIPATION.options
            },
            render: (_value, row) => PARTICIPATION.getIntroByCode(row.campaignRegistration.meetingType) ?? "--"
        },
        {
            key: "company",
            title: "企业抬头",
            minWidth: 280,
            search: { key: "keyword", placeholder: "请输入企业抬头/职务/姓名/手机号" },
            render: (_value, row) => row.campaignRegistration.companyLetterhead ?? "--"
        },
        { key: "applicants", title: "报名信息", width: 340, render: (_value, row) => <ApplicantSummary row={row} /> },
        {
            key: "fee",
            title: "报名费总计（元）",
            width: TABLE_COLUMN_WIDTH.WORD9,
            align: "right",
            headerAlign: "right",
            render: (_value, row) => formatRegistrationFee(row.campaignRegistration.registrationFee)
        },
        {
            key: "requirements",
            title: "参会需求",
            width: 300,
            render: (_value, row) => row.campaignRegistration.meetingRequirements || "--"
        },
        {
            key: "time",
            title: "活动时间范围",
            width: 270,
            render: (_value, row) =>
                row.campaign.validTimeStart && row.campaign.validTimeFinish
                    ? `${moment(row.campaign.validTimeStart).format("YYYY-MM-DD HH:mm")} 至 ${moment(row.campaign.validTimeFinish).format("YYYY-MM-DD HH:mm")}`
                    : "--"
        },
        {
            key: "campaignState",
            title: "活动状态",
            width: TABLE_COLUMN_WIDTH.WORD9,
            search: {
                key: "campaignState",
                type: "select",
                placeholder: "请选择活动状态",
                options: PC_ACTIVITY_STATE.options
            },
            render: (_value, row) => PC_ACTIVITY_STATE.getIntroByCode(row.campaign.state) ?? "未知状态"
        },
        {
            key: "state",
            title: "报名状态",
            width: TABLE_COLUMN_WIDTH.WORD9,
            search: {
                key: "state",
                type: "select",
                placeholder: "请选择报名状态",
                options: CAMPAIGN_REGISTRATION_STATE.options
            },
            render: (_value, row) =>
                CAMPAIGN_REGISTRATION_STATE.getIntroByCode(row.campaignRegistration.state) ?? "未知状态"
        },
        {
            key: "payType",
            title: "支付方式",
            width: TABLE_COLUMN_WIDTH.WORD9,
            render: (_value, row) => ORDER_PAY_TYPE.getIntroByCode(row.campaignRegistration.payType) ?? "--"
        },
        {
            key: "createdTime",
            title: "报名时间",
            width: TABLE_COLUMN_WIDTH.WORD9,
            render: (_value, row) =>
                row.campaignRegistration.createdTime
                    ? moment(row.campaignRegistration.createdTime).format("YYYY-MM-DD HH:mm")
                    : "--"
        },
        {
            key: "invoice",
            title: "发票凭证",
            width: 100,
            render: (_value, row) =>
                row.campaignRegistration.invoiceFileIds?.length ? (
                    <Button
                        className="registration-link"
                        type="link"
                        onClick={() => void viewFiles(row.campaignRegistration.invoiceFileIds, "发票单据")}
                    >
                        查看
                    </Button>
                ) : (
                    "--"
                )
        },
        {
            key: "operation",
            title: "操作",
            width: 270,
            fixed: "right",
            render: (_value, row) => {
                const config = row.meetingTypeConfig?.find(item => item.type === row.campaignRegistration.meetingType);
                const actions: RegistrationAction[] = [];
                if (row.campaignRegistration.state === CAMPAIGN_REGISTRATION_STATE.WAIT_PAY.code) {
                    actions.push({ key: "pay", label: "支付", onClick: () => void loadDialogRow(row, "payment") });
                }
                if (row.campaignRegistration.state !== CAMPAIGN_REGISTRATION_STATE.CANCELLED.code) {
                    actions.push({ key: "edit", label: "修改信息", onClick: () => void loadDialogRow(row, "edit") });
                }
                if (row.campaignRegistration.state === CAMPAIGN_REGISTRATION_STATE.WAIT_CONFIRM_PAY.code) {
                    actions.push({ key: "update-payment", label: "修改支付凭证", onClick: () => void loadDialogRow(row, "payment") });
                }
                if (
                    row.campaignRegistration.paymentVoucherFileIds?.length &&
                    row.campaignRegistration.state !== CAMPAIGN_REGISTRATION_STATE.WAIT_PAY.code
                ) {
                    actions.push({
                        key: "voucher",
                        label: "查看凭证",
                        onClick: () => void viewFiles(row.campaignRegistration.paymentVoucherFileIds, "查看凭证")
                    });
                }
                if (config?.contractTemplateFileId && row.campaignRegistration.state !== CAMPAIGN_REGISTRATION_STATE.CANCELLED.code) {
                    actions.push({ key: "contract", label: "合同模版", onClick: () => void downloadContract(row) });
                }
                return <RegistrationActionButtons actions={actions} />;
            }
        }
    ];
    return (
        <section className="registration-page section-card">
            <ProTable
                key={revision}
                title="报名列表"
                columns={columns}
                request={request}
                rowKey={row => row.campaignRegistration.id}
                onRequestError={() => message.error("报名列表加载失败")}
            />
            {activeRow && dialog === "payment" ? (
                <RegistrationPaymentDialog
                    open
                    data={activeRow}
                    onClose={() => setDialog(undefined)}
                    onSuccess={() => setRevision(value => value + 1)}
                />
            ) : null}
            {activeRow && dialog === "edit" ? (
                <RegistrationEditDialog
                    open
                    data={activeRow}
                    onClose={() => setDialog(undefined)}
                    onSuccess={() => setRevision(value => value + 1)}
                />
            ) : null}
            <RegistrationFileDialog
                files={files}
                title={fileTitle}
                onClose={() => {
                    setFileTitle("");
                    setFiles([]);
                }}
            />
        </section>
    );
}

/** 报名详情按基本信息、报名信息和参与人员分区展示，保留状态相关操作入口。 */
export function RegistrationDetailPage() {
    const navigate = useNavigate();
    const code = new URLSearchParams(location.search).get("id") ?? "";
    const [data, setData] = useState<CampaignRegistrationVo>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const [dialog, setDialog] = useState<"payment" | "edit">();
    const [dialogData, setDialogData] = useState<CampaignRegistrationVo>();
    const [files, setFiles] = useState<EntityFile[]>([]);
    const [fileTitle, setFileTitle] = useState("");
    const load = async () => {
        if (!code) {
            setError("缺少报名编号");
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const response = await campaignRegistrationDetail(code);
            setData(response.data);
            setError(undefined);
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : "报名详情加载失败");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void load();
    }, [code]);
    // 旧 Vue 报名详情首屏静默等待数据，不额外展示页面级加载框。
    if (loading) return null;
    if (error || !data) return <ErrorState message={error ?? "报名不存在"} onRetry={() => void load()} />;
    const registration = data.campaignRegistration;
    const state = CAMPAIGN_REGISTRATION_STATE.getMetaByCode(registration.state);
    const showVoucher = Boolean(
        registration.paymentVoucherFileIds?.length && registration.state !== CAMPAIGN_REGISTRATION_STATE.WAIT_PAY.code
    );
    const contractConfig = data.meetingTypeConfig?.find(item => item.type === registration.meetingType);
    const showContract = Boolean(
        contractConfig?.contractTemplateFileId && registration.state !== CAMPAIGN_REGISTRATION_STATE.CANCELLED.code
    );
    const openWorkflow = async (nextDialog: "payment" | "edit") => {
        const response = await campaignRegistrationDetail(registration.code);
        setDialogData(response.data);
        setDialog(nextDialog);
    };
    const viewFiles = async (ids: number[] | undefined, title: string) => {
        if (!ids?.length) {
            void message.error("暂无支付凭证");
            return;
        }
        setFiles(await fileService.getFileByIds(ids));
        setFileTitle(title);
    };
    const downloadContract = async () => {
        if (!contractConfig?.contractTemplateFileId) return;
        await downloadRegistrationContract(contractConfig.contractTemplateFileId);
    };
    const blocks: DetailJsonBlock<CampaignRegistrationVo>[] = [
        {
            sectionTitle: "基本信息",
            labelPlacement: "top",
            list: [
                { label: "活动名称", key: "campaign.title", span: 4 },
                { label: "活动状态", value: PC_ACTIVITY_STATE.getIntroByCode(data.campaign.state) || "--" },
                {
                    label: "活动时间范围",
                    value:
                        data.campaign.validTimeStart && data.campaign.validTimeFinish
                            ? `${moment(data.campaign.validTimeStart).format("YYYY-MM-DD HH:mm")} 至 ${moment(data.campaign.validTimeFinish).format("YYYY-MM-DD HH:mm")}`
                            : "--"
                },
                { label: "参会形式", value: PARTICIPATION.getIntroByCode(registration.meetingType) || "--" },
                { label: "企业抬头", value: registration.companyLetterhead || "--" },
                { label: "参会需求", value: registration.meetingRequirements || "--", span: 4 }
            ]
        },
        {
            sectionTitle: "报名信息",
            labelPlacement: "top",
            list: [
                { label: "参会人数", value: data.meetingCount ? `${data.meetingCount} 位` : "--" },
                {
                    label: "报名费总计（元）",
                    value: registration.registrationFee ? formatRegistrationFee(registration.registrationFee) : "--"
                },
                {
                    label: "支付方式",
                    value: ORDER_PAY_TYPE.getIntroByCode(registration.payType) || "--",
                    show: Boolean(registration.payType)
                },
                {
                    label: "支付凭证",
                    value: (
                        <Button
                            className="registration-link"
                            type="link"
                            onClick={() => void viewFiles(registration.paymentVoucherFileIds, "查看凭证")}
                        >
                            查看
                        </Button>
                    ),
                    show: showVoucher
                },
                {
                    label: "发票单据",
                    value: (
                        <Button
                            className="registration-link"
                            type="link"
                            onClick={() => void viewFiles(registration.invoiceFileIds, "发票单据")}
                        >
                            查看
                        </Button>
                    ),
                    show: Boolean(registration.invoiceFileIds?.length)
                },
                {
                    span: 4,
                    render: () => (
                        <div className="registration-detail-attendees">
                            {data.campaignRegistrationApplicant?.map(applicant => (
                                <DetailApplicantLine key={applicant.id} applicant={applicant} />
                            ))}
                        </div>
                    )
                }
            ]
        },
        {
            sectionTitle: "合同信息",
            labelPlacement: "top",
            show: showContract,
            list: [
                {
                    label: "合同模版",
                    value: (
                        <Button className="registration-link" type="link" onClick={() => void downloadContract()}>
                            查看/下载
                        </Button>
                    )
                }
            ]
        }
    ];
    return (
        <section className="registration-page registration-detail-page detail-container">
            <DetailHeader
                title="报名详情"
                menu={<Breadcrumb routes={[{ name: "我的报名", path: "/registration" }, { name: "报名详情" }]} />}
                leftInfos={[{ label: "报名编号", value: registration.code }]}
                rightInfos={[
                    {
                        label: "报名时间",
                        value: registration.createdTime
                            ? moment(registration.createdTime).format("YYYY-MM-DD HH:mm:ss")
                            : "--"
                    }
                ]}
                state={{ text: state?.intro }}
            />
            <DetailJson columns={blocks} data={data} />
            <Space className="registration-actions">
                {registration.state === CAMPAIGN_REGISTRATION_STATE.WAIT_PAY.code ? (
                    <Button type="primary" onClick={() => void openWorkflow("payment")}>
                        支付
                    </Button>
                ) : null}
                {registration.state === CAMPAIGN_REGISTRATION_STATE.WAIT_CONFIRM_PAY.code ? (
                    <Button type="primary" onClick={() => void openWorkflow("payment")}>
                        修改支付凭证
                    </Button>
                ) : null}
                {registration.state !== CAMPAIGN_REGISTRATION_STATE.CANCELLED.code ? (
                    <Button type="primary" onClick={() => void openWorkflow("edit")}>
                        修改信息
                    </Button>
                ) : null}
                <Button type="primary" onClick={() => navigate("/registration/myRegistration")}>
                    返回
                </Button>
            </Space>
            {dialog === "payment" && dialogData ? (
                <RegistrationPaymentDialog
                    open
                    data={dialogData}
                    onClose={() => setDialog(undefined)}
                    onSuccess={() => void load()}
                />
            ) : null}
            {dialog === "edit" && dialogData ? (
                <RegistrationEditDialog
                    open
                    data={dialogData}
                    onClose={() => setDialog(undefined)}
                    onSuccess={() => void load()}
                />
            ) : null}
            <RegistrationFileDialog
                files={files}
                title={fileTitle}
                onClose={() => {
                    setFileTitle("");
                    setFiles([]);
                }}
            />
        </section>
    );
}
